import React, { Component } from 'react';
import { connect } from 'react-redux';
import { normalize, schema } from 'normalizr';
import { loadWeekSessions, loadUnpagedChildSessions, updateChildSession } from './actions';
import { fetchWorkshop } from '../workshops/actions';
import authUtils from '../../auth/utils';
import { withTranslation } from 'react-i18next';
import { Panel, Button } from 'react-bootstrap';
import {
	sendMessage,
	PARENT_SEND_MESSAGE_REQUEST, PARENT_SEND_MESSAGE_SUCCESS, PARENT_SEND_MESSAGE_FAILURE
} from '../parents/actions';
import SendMessageButton from '../../common/components/message/SendMessageButton';
import BackButton from '../../common/components/BackButton';
import Table from './Table';
import InfoDialog from '../attendance/InfoDialog';
import NotesDialog from '../attendance/NotesDialog';
import DateSelector from '../../common/components/DateSelector';
import ViewSwitch from '../../common/components/ViewSwitch';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faPrint } from '@fortawesome/free-solid-svg-icons';
import PrintView from './PrintView';
import ui from '../../common/services/ui';

const WeekView = {
	WEEK: 'Week',
	DAY: 'Day'
};

class ManageWeekAttendance extends Component {

	constructor(props) {
		super(props);
		this.handleSessionClick = this.handleSessionClick.bind(this);
		this.handleMultipleSelectionChange = this.handleMultipleSelectionChange.bind(this);
		this.handleSendMessage = this.handleSendMessage.bind(this);
		this.handleShowInfo = this.handleShowInfo.bind(this);
		this.handleShowNotes = this.handleShowNotes.bind(this);
		this.handleWeekViewChange = this.handleWeekViewChange.bind(this);
		this.handleDateChange = this.handleDateChange.bind(this);
		this.hideInfo = this.hideInfo.bind(this);
		this.hideNotes = this.hideNotes.bind(this);
		this.handlePrint = this.handlePrint.bind(this);
		this.state = {
			showInfo: false,
			showNotes: false,
			selectedChildren: [],
			view: ui.isMobileView() ? WeekView.DAY : WeekView.WEEK,
			date: null
		};
	}

	componentDidMount() {
		this.loadData();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.weekId !== this.props.weekId) {
			this.loadData();
		}
	}

	loadData() {
		const { loadWeekSessions, loadUnpagedChildSessions, fetchWorkshop, weekId, workshopId } = this.props;
		loadWeekSessions(weekId);
		loadUnpagedChildSessions({ weekId: weekId, cancelled: false })
		fetchWorkshop(workshopId);
	}

	handleSessionClick(session) {
		const { updateChildSession } = this.props;
		const onSuccessEvent = () => this.loadData();
		updateChildSession(session.id, { ...session, isAttended: !session.isAttended }, onSuccessEvent);
	}

	handleMultipleSelectionChange(newSelectedChildren) {
		this.setState({ selectedChildren: newSelectedChildren });
	}

	handleShowInfo(parent) {
		this.setState({ showInfo: true, selectedParent: parent });
	}

	hideInfo() {
		this.setState({ showInfo: false });
	}

	handleShowNotes(child) {
		this.setState({ showNotes: true, selectedChild: child });
	}

	hideNotes() {
		this.setState({ showNotes: false });
	}

	handleSendMessage(values, onSuccess) {
		const selectedParentIds = [...new Set(this.state.selectedChildren.map(child => child.parent.id))];
		this.props.sendMessage({ ...values, parentIds: selectedParentIds }, onSuccess);
	}

	handleWeekViewChange(value) {
		this.setState({ view: value });
	}

	handleDateChange(value) {
		this.setState({ date: value });
	}

	handlePrint() {
		window.print();
	}

	normalizeData(children) {
		const parent = new schema.Entity('parents', {}, {
			idAttribute: c => c.id
		});
		const child = new schema.Entity('children', { parent: parent }, {
			idAttribute: p => p.id
		});
		const normalizedData = normalize(children, [child]);
		return normalizedData;
	}

	renderPanelHeader() {
		const { weekName, workshopName } = this.props;

		const icon = <FontAwesomeIcon icon={faCalendarDays} key='icon' />;

		const text = `${workshopName || ''} · ${weekName || ''}`;

		return [icon, text];;
	}

	render() {
		const { isReadonly, sessionDates, childSessions, canSendMessage, canViewNotes, workshopCenter,
			weekSessions, enableCollectMedicalInformation, t } = this.props;

		const normalizedData = this.normalizeData(this.state.selectedChildren);
		const selectedParents = (normalizedData && normalizedData.entities && normalizedData.entities.parents && Object.values(normalizedData.entities.parents)) || [];

		const today = moment().startOf('date');
		const todaySessionDate = sessionDates.find(x => today.isSame(x));

		const selectedView = this.state.view;
		const selectedDate = this.state.date || todaySessionDate || sessionDates[0];

		const isSingleDate = selectedView === WeekView.DAY || (sessionDates && sessionDates.length === 1);

		const visibleSessionDates = selectedView === WeekView.WEEK ? sessionDates : [selectedDate];
		const visibleChildSessions = selectedView === WeekView.WEEK ? childSessions : childSessions.filter(x => x.session && x.session.date === selectedDate);

		return <React.Fragment>
			<PrintView
				workshopName={this.props.workshopName}
				weekName={this.props.weekName}
				childSessions={visibleChildSessions}
				sessionDates={visibleSessionDates}
				enableCollectMedicalInformation={enableCollectMedicalInformation}
				t={t}
			/>
			<div className='no-print'>
				<div className='clearfix'>
					<BackButton className='mb-1'>{t('Common_BackToList')}</BackButton>
					{canSendMessage && <SendMessageButton style={{ marginLeft: '5px', marginBottom: '20px' }}
						onSendMessage={this.handleSendMessage}
						to={selectedParents.map(p => ({ name: p.name, email: p.email }))}
						center={workshopCenter}
						block={PARENT_SEND_MESSAGE_REQUEST}
						unblock={[PARENT_SEND_MESSAGE_SUCCESS, PARENT_SEND_MESSAGE_FAILURE]}
					/>}
					<Button bsStyle='info' className='mb-1' style={{ marginLeft: '5px' }} onClick={this.handlePrint}>
						<FontAwesomeIcon icon={faPrint} /> {t('Attendance_PrintFriendlyVersion')}
					</Button>
					<ViewSwitch className='pull-right mb-1' style={{ marginRight: 10 }}
						views={Object.values(WeekView).map(x => ({ name: x, label: t(`WorkshopWeekView_${x}`) }))}
						selectedView={selectedView}
						onChange={this.handleWeekViewChange}
					/>
					{selectedView === WeekView.DAY && <DateSelector className='pull-right mb-1' style={{ marginRight: 10 }}
						dates={sessionDates}
						selectedDate={selectedDate}
						onChange={this.handleDateChange}
					/>}
				</div>
				<Panel>
					<Panel.Heading>{this.renderPanelHeader()}</Panel.Heading>
					<Panel.Body>
						{isSingleDate && <h4 className='mr-2'>{ui.customDateFormat(selectedDate, 'LL')}</h4>}
						<Table showHeader={!isSingleDate}
							sessionDates={visibleSessionDates}
							weekSessions={weekSessions}
							isReadonly={isReadonly}
							childSessions={visibleChildSessions}
							onSessionClick={this.handleSessionClick}
							onInfoClick={this.handleShowInfo}
							onNotesClick={this.handleShowNotes}
							selectedChildren={this.state.selectedChildren}
							onMultipleSelectionChange={this.handleMultipleSelectionChange}
							canSendMessage={canSendMessage}
							canViewNotes={canViewNotes}
							weekView={selectedView}
							enableCollectMedicalInformation={enableCollectMedicalInformation}
						/>
					</Panel.Body>
				</Panel>
				<InfoDialog show={this.state.showInfo} t={t} parent={this.state.selectedParent} onHide={this.hideInfo} />
				<NotesDialog
					show={this.state.showNotes}
					onHide={this.hideNotes}
					t={t}
					child={this.state.selectedChild}
					onNotesUpdated={() => this.loadData()}
				/>
			</div>
		</React.Fragment>;
	}
}

const mapStateToProps = (state, ownProps) => {
	const weekId = Number(ownProps.match.params.weekId);
	const workshopId = Number(ownProps.match.params.workshopId);

	const {
		entities: {
			weekSessions, children, childWorkshopSessions, workshops, centers
		},
		configuration: { franchisor },
		auth: { currentUser }
	} = state;

	const childSessions = Object.values(childWorkshopSessions).filter(s => s.session && s.session.weekId === weekId);
	const workshop = workshops[workshopId];
	const weeks = workshop && workshop.weeks;
	const week = weeks && Object.values(weeks).find(w => w.id === weekId);

	const currentWeekSessions = weekSessions[weekId] && weekSessions[weekId].sessions || [];

	return {
		weekId,
		workshopId,
		weekName: week && week.name,
		workshopName: workshop && workshop.name,
		childSessions,
		weekSessions: currentWeekSessions,
		sessionDates: [...new Set(currentWeekSessions.map(item => item.date))],
		children,
		isReadonly: authUtils.hasReadOnlyPermission(currentUser, 'ChildWorkshopSessions'),
		franchisorId: franchisor.id,
		canSendMessage: franchisor.enableAdHocMessages && authUtils.hasPermission(currentUser, 'SendMessage'),
		canViewNotes: authUtils.hasPermission(currentUser, 'Notes'),
		workshopCenter: workshop && centers[workshop.centerId],
		enableCollectMedicalInformation: franchisor && franchisor.enableCollectMedicalInformation
	};
};

const mapDispatchToProps = {
	loadUnpagedChildSessions,
	loadWeekSessions,
	fetchWorkshop,
	updateChildSession,
	sendMessage
};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ManageWeekAttendance));